/**
 * Pure helpers used by DiscrepancyReviewDialog. Extracted here so they can
 * be unit-tested without pulling in the full dialog module (Apollo queries,
 * MUI components, etc.).
 *
 * Helpers accept structural types (not the derived GraphQL `Discrepancy`
 * type) so tests can pass minimal fixtures without depending on codegen.
 */
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import SyncIcon from "@mui/icons-material/Sync";
import {
  getFieldLabel,
  NULL_PRIOR_UNRESTORABLE_FIELDS,
} from "encamp-shared/src/waste/discrepancyFieldMeta";
import React from "react";

import { formatDateOnly } from "./discrepancyValueFormat";
import { documentDisagrees } from "./resolveWithValue.values";

export type DiscrepancyStatus = "unresolved" | "pending" | "resolved" | "ignored";

/**
 * Column-header text treatment shared by the four discrepancy panes: a step
 * smaller than the row text it labels, and bolder.
 *
 * Not the `variant="overline"` treatment the section titles use. Uppercase at
 * 1px tracking made the headers the same size as the body cells (both 14px) and
 * gave them the same treatment as the section title above them, so "DATA SOURCE
 * CONFLICTS" and "WASTE LINE" read at one level. Sentence case is also what the
 * app's own `DataGrid` headers use, which is the grid a reader arrives from.
 *
 * Each pane still sets its own cell's `minWidth`.
 */
export const COLUMN_HEADER_SX = {
  fontSize: "0.8125rem",
  fontWeight: 600,
  lineHeight: 1.5,
} as const;

/** Display order for a status breakdown — unresolved first, terminal statuses last. */
const STATUS_ORDER: readonly DiscrepancyStatus[] = ["unresolved", "pending", "resolved", "ignored"];

/** The minimal discrepancy shape the helpers operate on. */
export type DiscrepancyLike = {
  field: string;
  kind?: string | null;
  resolutionPath?: string | null;
  resolvedBy?: unknown;
  escalation?: string | null;
  status?: string | null;
  vendorAlignmentSuspect?: boolean | null;
  vendorWasteLinesUnmatched?: boolean | null;
};

// --- Field label map ---
// Source of truth is encamp-shared; re-exported under the same names so
// existing importers in this app don't need to change.

export { FIELD_LABELS, getFieldLabel } from "encamp-shared/src/waste/discrepancyFieldMeta";

// --- "Update vendor data" eligibility ---

/**
 * Discrepancy fields the "Update vendor data" bulk action cannot apply. These
 * have no single source value to copy into vendor data: structural rows
 * (wasteLinesCount, unpairedWasteLine), compliance rows (eManifestMissing), and
 * the within-line box-13 ↔ box-9 consistency flag (wasteCodeDotConsistency).
 * shippedDate is NOT blocked — the resolver supports it via a partition-aware
 * vendor correction.
 *
 * Mirrors BLOCKED_FIELDS in
 * apps/api/src/resolvers/waste/processedManifestCompliance.ts — the server
 * rejects these too, so they MUST be filtered client-side or the whole bulk
 * action fails.
 */
export const UPDATE_VENDOR_DATA_BLOCKED_FIELDS: ReadonlySet<string> = new Set([
  "eManifestMissing",
  "wasteLinesCount",
  "wasteCodeDotConsistency",
  "unpairedWasteLine",
]);

/** True when a discrepancy field can be resolved via "Update vendor data". */
export function supportsVendorDataUpdate(field: string): boolean {
  return !UPDATE_VENDOR_DATA_BLOCKED_FIELDS.has(field);
}

/** The statuses an undo can leave — mirrors UNDOABLE_STATUSES on the server. */
const UNDOABLE_STATUSES: ReadonlySet<string> = new Set(["RESOLVED", "IGNORED", "PENDING"]);

/** Shown when the status/path combination isn't one undo supports at all. */
const NOT_UNDOABLE_STATUS_REASON =
  "Only available for resolved discrepancies, non-discrepancies, and pending manual-entry resolutions";

/** `actorId` for events written by process-manifests rather than a person. */
const SYSTEM_ACTOR_ID = "system";

/**
 * The event fields the undo checks read. Structural, like every other helper
 * here, so a test can pass two plain objects.
 *
 * Ordered oldest first, matching the `seq: "asc"` the events dataloader
 * applies, so the latest event is the last element.
 */
export type UndoEventLike = {
  eventType: string;
  actorId: string;
  vendorValue?: string | null;
  manifestDocumentValue?: string | null;
  appliedTo?: readonly string[] | null;
};

/** The copies a write touched; VENDOR_DATA-path resolutions always wrote the vendor copy. */
function getWrittenAxes(
  latest: UndoEventLike,
  alwaysVendor: boolean
): { vendor: boolean; document: boolean } {
  return {
    vendor: alwaysVendor || (latest.appliedTo?.includes("VENDOR_DATA") ?? false),
    document: latest.appliedTo?.includes("DOCUMENT") ?? false,
  };
}

/** Why a missing prior value for this field cannot be put back, or null when empty is restorable. */
function getEmptyPriorBlock(field: string, axis: "vendor" | "document"): string | null {
  if (NULL_PRIOR_UNRESTORABLE_FIELDS.has(field)) {
    return "The previous waste codes were empty, so there is nothing to restore";
  }
  if (axis === "vendor" && field === "shippedDate") {
    return "The previous ship date was not recorded, so it cannot be restored";
  }
  return null;
}

/**
 * Whether a value the undo would restore is missing or unusable, judged from
 * the row's own history. Null when nothing here blocks the undo.
 *
 * Mirrors the server: `readPriorVendorEvent` (and, for a kept-open write,
 * `readKeptOpenWriteEvent`) reads the prior values off the latest event, and
 * only when that event is a person's `expectedEventType`. If the latest event
 * is anything else the server refuses for a different reason (recomputed since
 * resolution, resolved automatically), and those refusals stay at click time
 * because the row can change under an open dialog. The vendor and document
 * priors are each checked only when the write touched that copy.
 */
function getPriorValueBlock(
  d: Pick<DiscrepancyLike, "field">,
  events: readonly UndoEventLike[],
  opts: { expectedEventType: string; alwaysVendor: boolean }
): string | null {
  const latest = events.at(-1);
  if (!latest || latest.eventType !== opts.expectedEventType || isSystemEvent(latest)) {
    return null;
  }
  const axes = getWrittenAxes(latest, opts.alwaysVendor);
  if (axes.vendor && latest.vendorValue == null) {
    const block = getEmptyPriorBlock(d.field, "vendor");
    if (block) return block;
  }
  if (axes.document && latest.manifestDocumentValue == null) {
    return getEmptyPriorBlock(d.field, "document");
  }
  return null;
}

/** An event the pipeline wrote, which the server never lets an undo restore past. */
function isSystemEvent(event: UndoEventLike | undefined): boolean {
  return event?.actorId === SYSTEM_ACTOR_ID || event?.eventType === "AUTO_RESOLVED";
}

/**
 * A reviewer's write that left the row open: the latest event is a person's
 * VALUES_CHANGED that names the copies it wrote. Mirrors the server's
 * `readKeptOpenWriteEvent`; the undo event itself has no `appliedTo`, so a
 * second undo is refused.
 */
export function isKeptOpenWriteEvent(event: UndoEventLike | undefined): boolean {
  return (
    event?.eventType === "VALUES_CHANGED" &&
    event.actorId !== SYSTEM_ACTOR_ID &&
    (event.appliedTo?.length ?? 0) > 0
  );
}

/** Whether an UNRESOLVED row shows Undo: only while its latest event is a reviewer's write. */
export function hasKeptOpenWrite(events: readonly UndoEventLike[] | undefined): boolean {
  return isKeptOpenWriteEvent(events?.at(-1));
}

const KEPT_OPEN_UNDO_REASON = "Put back the values this save wrote";

function getKeptOpenUndoEligibility(
  d: Pick<DiscrepancyLike, "field">,
  events?: readonly UndoEventLike[]
): { enabled: boolean; reason: string } {
  if (!events) return { enabled: false, reason: "Checking whether this can be undone" };
  if (!hasKeptOpenWrite(events)) return { enabled: false, reason: NOT_UNDOABLE_STATUS_REASON };
  const block = getPriorValueBlock(d, events, {
    expectedEventType: "VALUES_CHANGED",
    alwaysVendor: false,
  });
  return block
    ? { enabled: false, reason: block }
    : { enabled: true, reason: KEPT_OPEN_UNDO_REASON };
}

/**
 * Refusal text for a MANUAL_ENTRY row the pipeline has already caught up
 * with — mirrors the server's readPriorVendorEvent, which refuses on the
 * same terms (latest event AUTO_RESOLVED, or written by "system").
 */
const MANUAL_ENTRY_AUTO_RESOLVED_REASON =
  "e-Manifest now matches this value, so undo would recreate a mismatch someone fixed in RCRAInfo. Resolve it again with the correct value instead.";

/** Refusal text when the latest event is neither the reviewer's own decision nor the pipeline's. */
const MANUAL_ENTRY_CHANGED_AFTER_REASON =
  "Values changed after this was saved. Re-review instead of undoing.";

/**
 * MANUAL_ENTRY eligibility, for RESOLVED and PENDING rows alike. Unlike
 * VENDOR_DATA, resolvedBy can't discriminate manual from automatic, so this
 * checks whether the latest event is the reviewer's own RESOLVED or PENDING.
 * The refusal splits the way the server's readPriorVendorEvent does: the
 * pipeline's own event means e-Manifest caught up, any other later event means
 * the values moved.
 */
function getManualEntryUndoEligibility(
  d: Pick<DiscrepancyLike, "status" | "field">,
  events?: readonly UndoEventLike[]
): { enabled: boolean; reason: string } {
  if (d.status !== "RESOLVED" && d.status !== "PENDING") {
    return { enabled: false, reason: "Only available for resolved or pending discrepancies" };
  }
  if (!events) {
    return { enabled: false, reason: "Loading resolution history…" };
  }
  const latest = events.at(-1);
  if (!latest || latest.eventType === "AUTO_RESOLVED") {
    return { enabled: false, reason: MANUAL_ENTRY_AUTO_RESOLVED_REASON };
  }
  if (latest.eventType !== d.status) {
    return { enabled: false, reason: MANUAL_ENTRY_CHANGED_AFTER_REASON };
  }
  if (latest.actorId === SYSTEM_ACTOR_ID) {
    return { enabled: false, reason: MANUAL_ENTRY_AUTO_RESOLVED_REASON };
  }
  const block = getPriorValueBlock(d, events, {
    expectedEventType: d.status,
    alwaysVendor: false,
  });
  if (block) return { enabled: false, reason: block };
  return { enabled: true, reason: "Put back the values this wrote and reopen as Pending Review" };
}

/**
 * Whether the "Undo" row action applies, with the text as the button's
 * tooltip. A non-discrepancy (IGNORED) always enables — it wrote nothing
 * locally. A RESOLVED or PENDING row serves the two paths that wrote local
 * data, VENDOR_DATA corrections and MANUAL_ENTRY resolutions, each with its
 * own eligibility check.
 *
 * `events` is the row's history, oldest first. Pass `undefined` while it is
 * still loading: the button blocks rather than enabling, so it can only ever
 * go from blocked to enabled and never offer an action it then takes away.
 * The cheap status/path checks run first, so a row refused on those never waits.
 *
 * This is an earlier copy of the server's checks, not a replacement. The
 * dialog can be stale; `assertUndoSafe` is the guarantee.
 *
 * Mirrors `validateUndoCandidate` in
 * packages/encamp-waste-data-access/src/discrepancies/undoResolution.ts.
 */
export function getUndoResolutionEligibility(
  d:
    | Pick<DiscrepancyLike, "status" | "kind" | "resolutionPath" | "resolvedBy" | "field">
    | undefined,
  events?: readonly UndoEventLike[]
): { enabled: boolean; reason: string } {
  if (d?.kind === "EPA_CORRECTION_REQUEST") {
    return {
      enabled: false,
      reason:
        "Tracks an e-Manifest correction request; its status follows that request, so it cannot be undone here",
    };
  }
  if (d?.status === "UNRESOLVED") {
    return getKeptOpenUndoEligibility(d, events);
  }
  if (!d?.status || !UNDOABLE_STATUSES.has(d.status)) {
    return { enabled: false, reason: NOT_UNDOABLE_STATUS_REASON };
  }
  if (d.status === "IGNORED") {
    return { enabled: true, reason: "Reopen this finding for review" };
  }
  if (d.status === "PENDING" && d.resolutionPath !== "MANUAL_ENTRY") {
    return { enabled: false, reason: NOT_UNDOABLE_STATUS_REASON };
  }
  if (d.resolutionPath === "MANUAL_ENTRY") {
    return getManualEntryUndoEligibility(d, events);
  }
  return getVendorDataUndoEligibility(d, events);
}

const AUTO_RESOLVED_REASON = "Resolved automatically, so there is nothing to restore";

/**
 * VENDOR_DATA (and pathless) RESOLVED rows. "Resolved automatically" is read
 * from the latest event's actor, as the server does: resolvedBy is a nullable
 * FK that clears when the staff user is deleted, which would misread a manual
 * correction as automatic.
 */
function getVendorDataUndoEligibility(
  d: Pick<DiscrepancyLike, "resolutionPath" | "field">,
  events?: readonly UndoEventLike[]
): { enabled: boolean; reason: string } {
  if (isSystemEvent(events?.at(-1))) {
    return { enabled: false, reason: AUTO_RESOLVED_REASON };
  }
  if (d.resolutionPath !== "VENDOR_DATA") {
    return { enabled: false, reason: "Nothing was written locally, so there is nothing to undo" };
  }
  if (!events) {
    return { enabled: false, reason: "Checking whether this can be undone" };
  }
  const priorValueBlock = getPriorValueBlock(d, events, {
    expectedEventType: "RESOLVED",
    alwaysVendor: true,
  });
  if (priorValueBlock) return { enabled: false, reason: priorValueBlock };

  return { enabled: true, reason: "Restore the prior vendor value and reopen this discrepancy" };
}

/**
 * Which PENDING row action to offer. A MANUAL_ENTRY row offers Undo, disabled
 * while its history loads; other PENDING rows, and an ineligible MANUAL_ENTRY
 * row, get the plain Reopen button.
 */
export function getPendingRowActions(
  d: Pick<DiscrepancyLike, "status" | "kind" | "resolutionPath" | "resolvedBy" | "field">,
  events?: readonly UndoEventLike[]
): { showUndo: boolean; showReopen: boolean } {
  if (d.resolutionPath !== "MANUAL_ENTRY") {
    return { showUndo: false, showReopen: true };
  }
  // History still loading (or failed): a plain Reopen would drop whatever
  // value the resolution wrote, so show the disabled Undo control instead of
  // offering an action that discards it.
  if (!events) {
    return { showUndo: true, showReopen: false };
  }
  // Reopen stays available beside Undo: the server can still refuse an Undo
  // the client thinks is allowed (a value edited since), and Reopen confirms first.
  const { enabled } = getUndoResolutionEligibility(d, events);
  return { showUndo: enabled, showReopen: true };
}

/**
 * Reopen on a PENDING MANUAL_ENTRY row leaves the value the resolution wrote
 * in place, so it asks first. Other PENDING rows reopen in one click.
 */
export function needsReopenConfirm(d: Pick<DiscrepancyLike, "status" | "resolutionPath">): boolean {
  return d.status === "PENDING" && d.resolutionPath === "MANUAL_ENTRY";
}

/** Reopen sits beside Undo when Undo is allowed, so only a refused Undo gets a "because". */
export function getReopenConfirmInfo(undo: { enabled: boolean; reason: string }): string {
  const stays = "Any vendor or document value this resolution wrote stays in place.";
  if (undo.enabled) return `${stays} Use Undo instead to put them back.`;
  return `${stays} Undo isn't available because: ${undo.reason.replace(/\.$/, "")}.`;
}

/** "Undo vendor correction" for VENDOR_DATA (unchanged copy); "Undo resolution" for MANUAL_ENTRY. */
export function getUndoActionLabel(resolutionPath?: string | null): string {
  return resolutionPath === "MANUAL_ENTRY" ? "Undo resolution" : "Undo vendor correction";
}

/**
 * The Undo confirmation dialog's body text. MANUAL_ENTRY describes what undo
 * actually restores and, when still PENDING, notes that RCRAInfo itself is
 * untouched.
 */
export function getUndoConfirmInfo(d: Pick<DiscrepancyLike, "status" | "resolutionPath">): string {
  if (d.status === "IGNORED") {
    return "The row goes back to Pending Review and needs a decision again. The history keeps both entries.";
  }
  if (d.status === "UNRESOLVED") {
    return "The vendor and manifest document values go back to what they were before this save. The row stays Pending Review.";
  }
  if (d.resolutionPath !== "MANUAL_ENTRY") {
    return "The vendor value recorded before the correction is written back and the discrepancy reopens. The event history keeps both entries.";
  }
  const base =
    "Any vendor or manifest document value this resolution wrote is put back to what it was before. The row goes back to Pending Review.";
  return d.status === "PENDING"
    ? `${base} This does not undo any change already made in RCRAInfo.`
    : base;
}

// --- Status derivation ---

/** A discrepancy is a compliance row if it has an escalation level. */
export function isComplianceDiscrepancy(d: { escalation?: string | null }): boolean {
  return d.escalation != null;
}

export function getDiscrepancyStatus(d: { status?: string | null }): DiscrepancyStatus {
  switch (d.status) {
    case "IGNORED":
      return "ignored";
    case "RESOLVED":
      return "resolved";
    case "PENDING":
      return "pending";
    default:
      return "unresolved";
  }
}

/**
 * The "Correct Vendor Data" toast, built from the rows the mutation returned.
 * The resolver leaves a row UNRESOLVED when the manifest document still
 * disagrees with the e-Manifest, so "updated" and "resolved" can differ. The
 * toast is a warning whenever any row stayed open.
 *
 * The resolver only accepts UNRESOLVED rows (assertEligibleForVendorDataUpdate), so
 * each returned row is either RESOLVED or kept open for the document. That is
 * why "updated − resolved" is the count the document still disagrees on.
 */
export function vendorDataUpdatedAlert(rows: { status: string | null }[]): {
  severity: "success" | "warning";
  message: string;
} {
  const updated = rows.length;
  if (updated === 0) {
    return {
      severity: "warning",
      message: "Vendor data update returned no rows — check their status before moving on",
    };
  }
  const resolved = rows.filter((d) => getDiscrepancyStatus(d) === "resolved").length;
  if (resolved === updated) {
    const noun = updated === 1 ? "discrepancy" : "discrepancies";
    return { severity: "success", message: `${updated} ${noun} resolved — vendor data updated` };
  }
  const rowNoun = updated === 1 ? "row" : "rows";
  return {
    severity: "warning",
    message: `Vendor data updated on ${updated} ${rowNoun} · ${resolved} resolved — the manifest document still disagrees on ${updated - resolved}`,
  };
}

/** Matches the subset of MUI Chip `color` values we use for status chips. */
export type StatusChipColor = "warning" | "info" | "success" | "default";

export function getStatusColor(status: DiscrepancyStatus): StatusChipColor {
  switch (status) {
    case "unresolved":
      return "warning";
    case "pending":
      return "info";
    case "resolved":
      return "success";
    case "ignored":
      return "default";
  }
}

/**
 * Customer-facing names for the lifecycle states. "Pending Review" rather than
 * "Unresolved" because the state means nobody has looked at the row yet, which
 * reads as Encamp's next step rather than the customer's problem; it and "Fix
 * pending" then run as a progression into "Resolved". Staff share the wording so
 * both sides of a support conversation name a state the same way.
 */
export function getStatusLabel(status: DiscrepancyStatus) {
  switch (status) {
    case "unresolved":
      return "Pending Review";
    case "pending":
      return "Fix pending";
    case "resolved":
      return "Resolved";
    case "ignored":
      return "Non-Discrepancy";
  }
}

// --- Action eligibility (resolve modal) ---
// Shared by DiscrepancyReviewDialog (the selected/single-row targets) and
// ResolveDiscrepanciesDialog (the "apply to others" matching rows) — one
// eligibility rule set for every row a resolve action might touch.

export type ActionKey =
  | "updateVendorData"
  | "resolveExternally"
  | "nonDiscrepancy"
  | "cantDetermine";

export type SkipGroup = {
  reason: string;
  count: number;
};

export type ActionEligibility = {
  eligibleIds: string[];
  skips: SkipGroup[];
};

/** A row `computeActionEligibility` can evaluate — narrower than the full generated Discrepancy type. */
export type EligibilityRow = DiscrepancyLike & { id: string };

export function computeActionEligibility(
  action: ActionKey,
  selected: readonly EligibilityRow[]
): ActionEligibility {
  const eligibleIds: string[] = [];
  const skipCounts = new Map<string, number>();
  const addSkip = (reason: string) => {
    skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + 1);
  };

  // "Can't Resolve — Mark as Blocked" blocks the whole manifest rather than acting on rows,
  // so every selected row is "eligible" — eligibility gating doesn't apply.
  if (action === "cantDetermine") {
    return { eligibleIds: selected.map((d) => d.id), skips: [] };
  }

  for (const d of selected) {
    if (action === "updateVendorData") {
      if (d.status === "IGNORED") {
        addSkip("already a non-discrepancy");
      } else if (d.status !== "UNRESOLVED") {
        addSkip("already resolved or pending");
      } else if (!supportsVendorDataUpdate(d.field)) {
        addSkip(`${getFieldLabel(d.field)} doesn't support vendor data update`);
      } else if (d.vendorAlignmentSuspect === true) {
        // Mirrors assertEligibleForVendorDataUpdate on the server: the two
        // sources report different waste line counts and the line numbers no
        // longer name the same waste, so there is no line to write the value
        // onto. Skipping here keeps the rest of a multi-row selection working;
        // the server throw is the backstop for a stale client.
        addSkip("vendor and e-Manifest waste lines can't be matched by line number");
      } else if (d.vendorWasteLinesUnmatched === true) {
        // Also mirrors assertEligibleForVendorDataUpdate: a per-line row left
        // over from before matching shipped, or from a reviewer removing a
        // pairing, was paired by line number even though the manifest's
        // vendor and e-Manifest line counts still differ. Same reason text as
        // the vendorAlignmentSuspect skip above so the two group together.
        addSkip("vendor and e-Manifest waste lines can't be matched by line number");
      } else {
        eligibleIds.push(d.id);
      }
    } else if (action === "resolveExternally") {
      if (d.status === "IGNORED") {
        addSkip("already a non-discrepancy");
      } else if (d.status === "RESOLVED") {
        addSkip("already resolved");
      } else if (d.status === "PENDING") {
        addSkip("already pending");
      } else {
        eligibleIds.push(d.id);
      }
    } else {
      if (d.status === "IGNORED") {
        addSkip("already a non-discrepancy");
      } else if (d.status === "RESOLVED") {
        addSkip("already resolved");
      } else if (isComplianceDiscrepancy(d)) {
        addSkip("compliance discrepancy — use Resolve in e-Manifest instead");
      } else if (d.status === "PENDING" && d.resolutionPath === "MANUAL_ENTRY") {
        // The server refuses this too: dismissing would clear the resolution
        // but leave the value it wrote in place, with no Undo afterwards.
        addSkip("pending manual resolution — undo or reopen it first");
      } else {
        eligibleIds.push(d.id);
      }
    }
  }

  const skips = [...skipCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return { eligibleIds, skips };
}

/**
 * Whether the resolve dialog has any action to offer for one row. Mirrors how
 * the dialog builds its action list, so a Resolve control and the dialog it
 * opens cannot disagree about what is possible: a resolved or dismissed row
 * would otherwise open a dialog with every action skipped.
 */
export function hasResolvableAction(row: EligibilityRow, includeVendorAction: boolean): boolean {
  const actions: ActionKey[] = includeVendorAction
    ? ["updateVendorData", "resolveExternally", "nonDiscrepancy"]
    : ["resolveExternally", "nonDiscrepancy"];
  return actions.some((action) => computeActionEligibility(action, [row]).eligibleIds.length > 0);
}

export function formatSkipSummary(skips: SkipGroup[]): string {
  return skips.map((s) => `• ${s.count} ${s.reason}`).join("\n");
}

/** First line of a formatSkipSummary ("• 2 reason") as the top skip reason, or null for none. */
export function topSkipFromSummary(summary: string | undefined, skipped: number): SkipGroup | null {
  const match = summary?.split("\n")[0]?.match(/^•\s*(\d+)\s+(.+)$/);
  if (match) return { reason: match[2], count: Number(match[1]) };
  return skipped > 0 ? { reason: "not eligible", count: skipped } : null;
}

// --- Status chip iconography ---

/**
 * Returns the icon element to render inside the status chip, or `undefined`
 * for a plain (icon-less) chip.
 */
export function getStatusChipIcon(
  d: Pick<DiscrepancyLike, "field" | "kind" | "resolutionPath" | "resolvedBy">,
  status: DiscrepancyStatus
): React.ReactElement<{ "aria-hidden"?: boolean }> | undefined {
  // Staff closed this row as never appearing in e-Manifest — a distinct icon
  // from BlockIcon (non-discrepancy) since this is a resolution, not a dismissal.
  if (status === "resolved" && d.resolutionPath === "NEVER_IN_EMANIFEST") {
    return <EventBusyIcon />;
  }
  // eManifestMissing rows show the missing-manifest icon only when active (unresolved/pending).
  // Terminal rows fall through to the generic status-based icons.
  if (d.field === "eManifestMissing" && (status === "unresolved" || status === "pending")) {
    return <ErrorOutlineIcon />;
  }
  // Dismissed as a non-discrepancy (terminal, user-driven).
  if (status === "ignored") return <BlockIcon />;
  // Manual vendor-data fix (a user resolved it locally).
  if (d.resolutionPath === "VENDOR_DATA" && d.resolvedBy) return <CheckIcon />;
  // Resolved at the source / auto-resolved.
  if (status === "resolved") return <PublishedWithChangesIcon />;
  if (status === "pending") return <SyncIcon />;
  return undefined;
}

/**
 * Tooltip text that explains *why* a chip has its current status/icon.
 * The icon alone can't communicate whether a resolved chip was manually
 * refreshed vs auto-resolved by process-manifests, or whether a pending chip is
 * waiting on new vendor data vs an exception report, so surface the detail
 * on hover.
 */
export function getStatusChipTooltip(
  d: Pick<DiscrepancyLike, "field" | "kind" | "resolutionPath" | "resolvedBy" | "escalation">,
  status: DiscrepancyStatus
): string | undefined {
  // Staff closed this row as never appearing in e-Manifest (e.g. the receiving
  // facility isn't a registered TSDF). Checked before the generic
  // eManifestMissing/EPA branches below so it isn't shadowed by them.
  if (status === "resolved" && d.resolutionPath === "NEVER_IN_EMANIFEST") {
    return "Will never be in e-Manifest";
  }

  // eManifestMissing rows show the missing-manifest message only when active (unresolved/pending).
  // Terminal rows fall through to the generic status-based tooltips.
  if (d.field === "eManifestMissing" && (status === "unresolved" || status === "pending")) {
    return "No matching e-Manifest record found for this manifest";
  }

  if (status === "ignored") {
    // resolvedBy present = a person dismissed it; absent = process-manifests
    // auto-ignored a mismatch that converged externally with no staff action.
    return d.resolvedBy
      ? "Marked as a non-discrepancy"
      : "Auto-ignored — data changed externally without staff action";
  }

  if (status === "resolved") {
    if (d.kind === "EPA_CORRECTION_REQUEST") {
      return "Corrected in e-Manifest";
    }
    if (d.resolutionPath === "EMANIFEST") {
      return "Corrected in e-Manifest";
    }
    if (d.resolutionPath === "VENDOR_DATA") {
      return "Vendor data corrected";
    }
    if (d.resolutionPath === "MANUAL_ENTRY") {
      // No resolvedBy: process-manifests resolved it once e-Manifest was fixed.
      return d.resolvedBy ? "Reviewer-chosen value" : "Corrected in e-Manifest";
    }
    return "Auto-resolved — values now match";
  }

  if (status === "pending") {
    if (d.escalation === "OVERDUE_60") return "Pending exception report";
    // Data could flow back from either side (vendor re-sync or eManifest
    // update in RCRAInfo) — stay source-agnostic.
    return "Waiting for the fix to show up on a later sync";
  }

  if (status === "unresolved") {
    return "Needs a decision";
  }

  return undefined;
}

// --- Status breakdown & terminal-last ordering ---
// The status filter that used to hide resolved/ignored rows is gone — every
// finding shows regardless of status. These two helpers keep a mixed-status
// list legible without it: one summarizes a section's mix in its header, the
// other keeps a group's actionable rows from getting lost among ones that
// don't need a decision.

/**
 * A section's status mix as a header label, e.g. `"3 pending review · 1 fix
 * pending"`, counting only non-zero statuses in unresolved/pending/resolved/
 * ignored order. Collapses to the plain count when only one status is present.
 */
export function getStatusBreakdownLabel(items: readonly { status: DiscrepancyStatus }[]): string {
  const counts = new Map<DiscrepancyStatus, number>();
  for (const item of items) {
    counts.set(item.status, (counts.get(item.status) ?? 0) + 1);
  }
  const present = STATUS_ORDER.filter((status) => (counts.get(status) ?? 0) > 0);
  if (present.length <= 1) return String(items.length);
  return present
    .map((status) => `${counts.get(status)} ${getStatusLabel(status).toLowerCase()}`)
    .join(" · ");
}

export interface SavedStatusCounts {
  resolved: number;
  pending: number;
  stayOpen: number;
}

/** Counts the statuses a resolve mutation returned: what the server did, not what the client predicted. */
export function countSavedStatuses(rows: readonly { status?: string | null }[]): SavedStatusCounts {
  const counts: SavedStatusCounts = { resolved: 0, pending: 0, stayOpen: 0 };
  for (const { status } of rows) {
    if (status === "RESOLVED") counts.resolved += 1;
    else if (status === "PENDING") counts.pending += 1;
    else if (status === "UNRESOLVED") counts.stayOpen += 1;
  }
  return counts;
}

export function formatDismissToast(count: number): string {
  return count === 1 ? "Marked 1 as Non-Discrepancy" : `Marked ${count} as Non-Discrepancies`;
}

/** True for a status that needs no further review action. */
function isTerminalStatus(item: { status?: string | null }): boolean {
  const status = getDiscrepancyStatus(item);
  return status === "resolved" || status === "ignored";
}

/**
 * Stable-sorts rows so unresolved/pending rows come before resolved/ignored
 * ones, preserving each half's existing (server) order. Reason: with the
 * status filter gone, a group's actionable rows would otherwise be scattered
 * among rows needing no decision.
 */
export function sortTerminalLast<T extends { status?: string | null }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => {
    const aTerminal = isTerminalStatus(a);
    const bTerminal = isTerminalStatus(b);
    if (aTerminal === bTerminal) return 0;
    return aTerminal ? 1 : -1;
  });
}

// --- Header context line ---

export interface ManifestContextFields {
  generatorName?: string | null;
  epaId?: string | null;
  tsdfName?: string | null;
  shippedDate?: string | null;
}

/**
 * The dialog subtitle naming the shipment: generator + its EPA id, the
 * receiving facility, and the ship date. Omits any missing segment and its
 * neighboring separator; returns null when nothing is available.
 */
export function formatManifestContextLine(fields: ManifestContextFields): string | null {
  const from = [fields.generatorName, fields.epaId].filter(Boolean).join(" · ");
  const to = fields.tsdfName ?? "";
  let route = "";
  if (from && to) {
    route = `${from} → ${to}`;
  } else {
    route = from || to;
  }
  const shipped = fields.shippedDate ? `shipped ${formatDateOnly(fields.shippedDate)}` : "";
  const line = [route, shipped].filter(Boolean).join(" · ");
  return line || null;
}

// --- Value formatting ---

export { formatDateOnly, formatDisplayValue, formatFieldValue } from "./discrepancyValueFormat";

/**
 * Determines which badge case applies to a field discrepancy row based on
 * which sources differ from the eManifest value.
 *
 * - `"vendor-only"`: only the vendor value differs
 * - `"doc-only"`: only the document value differs
 * - `"both"`: vendor and document both differ (worst case)
 * - `null`: no displayable badge (neither present or both match)
 */
export type DiscrepancyBadgeCase = "vendor-only" | "doc-only" | "both" | null;

export function getDiscrepancyBadgeCase(row: {
  eManifestValue?: string | null;
  vendorValue?: string | null;
  manifestDocumentValue?: string | null;
  vendorDiscrepant?: boolean | null;
  documentDiscrepant?: boolean | null;
}): DiscrepancyBadgeCase {
  // Prefer the persisted per-axis verdicts (computed by the normalization-aware
  // comparators). Fall back to raw value comparison only for legacy rows that
  // predate those columns (verdict === null), where raw inequality is the best
  // signal available until the row is next recomputed. Raw comparison ignores
  // field-specific normalization (zip, EPA-ID name match, case), so it can show
  // a spurious chip — the verdict columns exist precisely to stop that.
  const vendorDiffers =
    row.vendorDiscrepant ?? (row.vendorValue != null && row.vendorValue !== row.eManifestValue);
  const docDiffers = documentDisagrees(row);
  if (vendorDiffers && docDiffers) return "both";
  if (vendorDiffers) return "vendor-only";
  if (docDiffers) return "doc-only";
  return null;
}
